import { useMemo } from 'react';
import { Trans, useLingui } from '@lingui/react/macro';
import { format, parseISO } from 'date-fns';

import {
  faCheckCircle,
  faExclamationTriangle,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  InsuranceStatusCta,
  type ZuubPatientListItemV3,
} from '../../../@types/zuub/contracts';
import type { Appointment } from '../../../react-query/useAppointments';
import { formatCurrency } from '@/utils/currency';

import { getProviderName } from './helpers';
import {
  PrivateDateOfBirth,
  PrivatePatientName,
} from './privatePatientDetails';
import { usePreferences } from '@/react-query/usePreferences';

/** Pick primary plan: active plans first, then first plan. */
const getPrimaryPlan = (zuubInsurance: ZuubPatientListItemV3 | undefined) => {
  const plans = zuubInsurance?.plans ?? [];
  if (plans.length === 0) return undefined;
  const sorted = [...plans].sort((a, b) => {
    const aActive = a.insuranceStatusV2 === InsuranceStatusCta.ACTIVE;
    const bActive = b.insuranceStatusV2 === InsuranceStatusCta.ACTIVE;
    if (aActive !== bActive) return aActive ? -1 : 1;
    return 0;
  });
  return sorted[0];
};

export const PatientCell = ({
  appointment,
  zuubInsurance,
}: {
  appointment: Appointment;
  zuubInsurance?: ZuubPatientListItemV3;
}) => {
  const { i18n, t } = useLingui();
  const { data: prefs } = usePreferences();
  const isInsuranceEligibilityEnabled = prefs?.isInsuranceEligibilityEnabled;

  const primaryPlan = useMemo(
    () => getPrimaryPlan(zuubInsurance),
    [zuubInsurance],
  );

  return (
    <>
      <PrivatePatientName appointment={appointment} />
      <p className="vai-typography-p vai-text-lg">
        {format(parseISO(appointment.appointment.start), 'hh:mm aaa')}
      </p>
      <div className="vai-mt-1">
        <p className="vai-typography-small !vai-font-normal">
          <Trans>Operatory: </Trans>
          {appointment.appointment.operatory?.name ?? t`Unknown`}
        </p>
        {appointment.patient.isNewPatient && (
          <p className="vai-typography-small !vai-font-normal">
            <Trans>NEW</Trans>
          </p>
        )}
        <p className="vai-typography-small !vai-font-normal">
          <Trans>Provider: </Trans>
          {getProviderName(appointment.appointment.provider)}
        </p>
        <PrivateDateOfBirth appointment={appointment} />
        {appointment.appointment.imagesAt && (
          <p className="vai-typography-small !vai-font-normal">
            <Trans>Last images taken </Trans>
            {i18n.date(new Date(appointment.appointment.imagesAt), {})}
          </p>
        )}

        {isInsuranceEligibilityEnabled && primaryPlan && (
          <div className="vai-mt-2 vai-border-t vai-border-zinc-200 vai-pt-1">
            <p className="vai-typography-small !vai-font-normal">
              <Trans>Insurance:</Trans> {primaryPlan.payerName ?? '--'}
            </p>
            <p className="vai-typography-small !vai-font-normal vai-inline-flex vai-items-center vai-gap-1">
              <Trans>Status:</Trans>{' '}
              {primaryPlan.insuranceStatusV2 === InsuranceStatusCta.ACTIVE && (
                <FontAwesomeIcon
                  icon={faCheckCircle}
                  className="vai-text-zinc-700 vai-text-[10px]"
                />
              )}
              {primaryPlan.insuranceStatusV2 != null &&
                primaryPlan.insuranceStatusV2 !== InsuranceStatusCta.ACTIVE && (
                  <FontAwesomeIcon
                    icon={faExclamationTriangle}
                    className="vai-text-zinc-700 vai-text-[10px]"
                  />
                )}
              {primaryPlan.insuranceStatusV2 === InsuranceStatusCta.ACTIVE
                ? t`Active`
                : primaryPlan.insuranceStatusV2 != null
                  ? t`Inactive`
                  : '--'}
            </p>
            <p className="vai-typography-small !vai-font-normal">
              <Trans>Benefits Remaining:</Trans>{' '}
              {primaryPlan.remainingBenefits != null
                ? formatCurrency(primaryPlan.remainingBenefits)
                : '--'}
            </p>
            <p className="vai-typography-small !vai-font-normal">
              <Trans>Deductible Remaining:</Trans>{' '}
              {primaryPlan.remainingDeductible != null
                ? formatCurrency(primaryPlan.remainingDeductible)
                : '--'}
            </p>
          </div>
        )}
      </div>
    </>
  );
};
